import classNames from 'classnames';
import { ReactNode } from 'react';
import { Avatar } from '../Avatar';
import { Image } from '../Image';
import { PopupDialog } from '../PopupDialog';
import { ReadValue } from '../ReadValue';

export interface InfoCardField {
  label: string;
  value: ReactNode;
  onClick?: () => void;
  className?: string;
}

export interface InfoCardProps {
  /** Primary title */
  title?: string;
  /** Subtitle displayed below title (e.g., deployment location) */
  subtitle?: ReactNode;
  /** Image URL or array of images for Gallery */
  image?: string | string[];
  /** Placeholder component when no image is provided */
  imagePlaceholder?: ReactNode;
  /** Size of the image container */
  imageSize?: string;
  /** Array of fields to display */
  fields?: InfoCardField[];
  /** Status badge configuration */
  status?: string;
  /** Additional content to render below fields */
  children?: ReactNode;
  /** Custom className for the container */
  className?: string;
  /** Modal configuration */
  modal?: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    content: ReactNode;
  };
}

export const InfoCard = ({
  title,
  subtitle,
  image,
  imagePlaceholder,
  imageSize = 'h-32 w-32',
  fields = [],
  children,
  className,
  modal,
}: InfoCardProps) => {
  const handleFieldClick = (field: InfoCardField) => {
    if (field.onClick) {
      field.onClick();
    }
  };

  // Determine what to show in the image area
  const renderImageArea = () => {
    // If we have an explicit imagePlaceholder, prioritize that
    if (imagePlaceholder) {
      return imagePlaceholder;
    }

    // If we have an image, try to show it with fallback handling
    if (image) {
      const imageUrl = Array.isArray(image) ? image[0] : image;
      return (
        <Image
          url={imageUrl}
          size='fit'
          onImageError={() => {
            // Image component will handle the error state internally
          }}
        />
      );
    }

    // If no image and no explicit placeholder, show Avatar with initials from title
    return (
      <Avatar
        image={null}
        name={title}
        size='full'
        square
        className='rounded-md'
      />
    );
  };

  return (
    <>
      <div
        className={classNames(
          'relative flex gap-5 rounded-md border border-grey-20 p-3',
          className
        )}
      >
        {/* Image Section */}
        <div className={classNames(imageSize, 'overflow-hidden rounded')}>
          {renderImageArea()}
        </div>

        {/* Content Section */}
        <div className='flex flex-col gap-1'>
          {/* Title */}
          <p className='pt-0.5 text-lg font-medium text-primary'>{title}</p>

          {/* Subtitle (e.g., deployment location) */}
          {subtitle}

          {/* Fields */}
          {fields.map((field, index) => (
            <div key={index} className=''>
              <span className='text-sm text-tertiary'>{field.label}</span>
              {field.onClick ? (
                <p
                  className={classNames(
                    'text cursor-pointer text-wrap leading-[17.5px] hover:underline',
                    field.className
                  )}
                  onClick={() => handleFieldClick(field)}
                >
                  {field.value}
                </p>
              ) : (
                <p
                  className={classNames(
                    'text-wrap leading-[17.5px]',
                    field.className
                  )}
                >
                  {field.value}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Additional Content */}
      {children}

      {/* Modal */}
      {modal && (
        <PopupDialog isOpen={modal.isOpen} onClose={modal.onClose}>
          <PopupDialog.Title>{modal.title}</PopupDialog.Title>
          {modal.content}
        </PopupDialog>
      )}
    </>
  );
};

// Convenience component for adding ReadValue fields after the card
InfoCard.ReadValue = ReadValue;
